/**
 * The outbox: everything the field wrote that the server has not accepted yet.
 *
 * ── What it is for ───────────────────────────────────────────────────────────
 *
 * §J: "preserve unsent notes locally, preserve inspection entries locally,
 * upload photos when connection returns, prevent accidental duplicate
 * submissions". Vieques connectivity is not a degraded version of connectivity;
 * it is a dead spot with a hill in it. A screen that posts on tap and shows a
 * spinner is a screen that loses the tap.
 *
 * So nothing in Intake or Inspection Mode posts directly. Every write is
 * enqueued with its own `client_id` and the screen updates immediately. The
 * queue drains when it can.
 *
 * ── Why the client_id is the whole design ────────────────────────────────────
 *
 * `inspections.ts` and `ingest.ts` are both idempotent on a caller-supplied id:
 * `ON CONFLICT (client_id) DO NOTHING` for entries, the three-key `findExisting`
 * for listings. That is what makes a replay safe. A retry after a response that
 * never arrived is a server-side no-op rather than a second inspection entry, so
 * this file can retry without ever having to know whether the previous attempt
 * landed — which, on a connection that drops mid-response, is unknowable.
 *
 * The id is also the DUPLICATE GUARD on this side: `enqueue` refuses an id it
 * already holds or has recently completed, so a double-tapped SAVE button
 * produces one queue item, not two.
 *
 * ── Aliases, and why an inspection entry can be queued before the inspection ──
 *
 * Inspection Mode opens in a dead spot. The inspection does not exist on the
 * server yet, so entries cannot name its id. The first item declares
 * `provides: { alias: 'inspection', field: 'inspectionId' }`; the entries carry
 * `{{inspection}}` in their path. The queue is strictly FIFO, so by the time an
 * entry is sent the inspection has answered and the alias is filled. If it never
 * can — a 4xx that will never succeed — the alias is marked dead and everything
 * waiting on it fails immediately and visibly, rather than retrying an
 * unresolvable path forever.
 */

import { api, type ApiError } from './api'
import { idbAll, idbDelete, idbPut, OUTBOX_STORE, openFieldDb } from './photoQueue'
import * as photoQueue from './photoQueue'

export type OutboxKind = 'intake' | 'note' | 'inspection' | 'entry' | 'finish'

export interface OutboxItem {
  /** The `client_id`. Also the duplicate guard on this side. */
  id: string
  kind: OutboxKind
  method: 'POST' | 'PATCH'
  /** May contain `{{alias}}`, filled from an earlier item's response. */
  path: string
  body: Record<string, unknown>
  /** This item's response fills an alias for later items. */
  provides?: { alias: string; field: string }
  /** `photoQueue` keys whose uploads must complete before the body is sent. */
  photos?: string[]
  /** Where the resulting upload ids go: `uploadId` (one) or `images` (a list). */
  photoField?: 'uploadId' | 'images'
  /** Upload ids already obtained, so a retry never re-uploads bytes. */
  uploaded?: string[]
  /** R2 folder, e.g. `inspection`. */
  purpose?: string
  opportunityId?: string | null
  /** One short line for the queue list. Never a note body or a phone number. */
  label?: string
  createdAt: number
  attempts: number
  nextAttemptAt: number
  lastError?: string | null
  /** Set when the server refused in a way that retrying cannot fix. */
  dead?: boolean
}

export interface OutboxState {
  /** Items still trying. This is the "N unsent" badge. */
  pending: number
  /** Items the server refused permanently. They need a person, not a retry. */
  failed: number
  sending: boolean
  online: boolean
  /** The oldest pending item's timestamp, so a screen can say how long it has waited. */
  oldestAt: number | null
  lastError: string | null
  /** False when the queue is in memory only — closing the tab would lose it. */
  durable: boolean
}

const LS_KEY = 'hh.outbox'
const LS_ALIASES = 'hh.outbox.aliases'
const LS_DONE = 'hh.outbox.done'
/** How many completed ids to remember, for the duplicate guard. */
const DONE_RING = 200
const FLUSH_EVERY_MS = 20_000
const MAX_ATTEMPTS = 12

/* ────────────────────────────────────────────────────────────────────────────
 * State
 * ────────────────────────────────────────────────────────────────────────── */

let items: OutboxItem[] = []
let aliases: Record<string, string> = {}
let deadAliases = new Set<string>()
let done: string[] = []
let loaded: Promise<void> | null = null
let flushing = false
/**
 * Something was enqueued (or asked to flush) WHILE a flush was in flight.
 *
 * Without this the field loses taps in the most ordinary way there is: the
 * queue starts draining, somebody presses PASS, `flush` sees `flushing` and
 * returns, and that entry then waits for the twenty-second timer — or, if the
 * screen is closed first, for the next visit. The pass below iterates a
 * SNAPSHOT of the queue, so an item added mid-pass is not in it either.
 */
let flushWanted = false
let lastError: string | null = null
let usingIdb = false
let timer: ReturnType<typeof setInterval> | null = null
let wired = false

const listeners = new Set<() => void>()
let snapshot: OutboxState = {
  pending: 0, failed: 0, sending: false, online: true, oldestAt: null, lastError: null, durable: false,
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false
}

function recompute(): void {
  const pending = items.filter((i) => !i.dead)
  const next: OutboxState = {
    pending: pending.length,
    failed: items.length - pending.length,
    sending: flushing,
    online: isOnline(),
    oldestAt: pending.length ? Math.min(...pending.map((i) => i.createdAt)) : null,
    lastError,
    durable: usingIdb,
  }
  // `useSyncExternalStore` settles snapshots with Object.is, so a new object on
  // every read would re-render forever. Only replace it when a field moved.
  const changed = (Object.keys(next) as (keyof OutboxState)[]).some((k) => next[k] !== snapshot[k])
  if (!changed) return
  snapshot = next
  for (const fn of [...listeners]) fn()
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function getState(): OutboxState {
  return snapshot
}

/** The queue itself, oldest first — for the "unsent" sheet. */
export function pendingItems(): OutboxItem[] {
  return [...items].sort((a, b) => a.createdAt - b.createdAt)
}

/* ────────────────────────────────────────────────────────────────────────────
 * Persistence
 * ────────────────────────────────────────────────────────────────────────── */

function lsGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    // Corrupt JSON or a private window. Starting empty loses queued work, which
    // is bad — but throwing here would white-screen the field app, which is worse.
    return fallback
  }
}

function lsSet(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* Quota or a private window: the queue survives in memory for this page. */
  }
}

async function persist(item?: OutboxItem): Promise<void> {
  if (usingIdb) {
    if (item) await idbPut(OUTBOX_STORE, item)
    else for (const row of items) await idbPut(OUTBOX_STORE, row)
  } else {
    lsSet(LS_KEY, items)
  }
  lsSet(LS_ALIASES, { aliases, dead: [...deadAliases] })
  lsSet(LS_DONE, done)
}

async function forget(id: string): Promise<void> {
  items = items.filter((i) => i.id !== id)
  if (usingIdb) await idbDelete(OUTBOX_STORE, id)
  else lsSet(LS_KEY, items)
}

/** Reads the queue back off the device. Safe to call repeatedly. */
export function load(): Promise<void> {
  if (loaded) return loaded
  loaded = (async () => {
    const db = await openFieldDb()
    usingIdb = db !== null
    const rows = usingIdb ? await idbAll<OutboxItem>(OUTBOX_STORE) : null
    if (rows) {
      items = rows
    } else {
      usingIdb = false
      items = lsGet<OutboxItem[]>(LS_KEY, [])
    }
    const held = lsGet<{ aliases?: Record<string, string>; dead?: string[] }>(LS_ALIASES, {})
    aliases = held.aliases ?? {}
    deadAliases = new Set(held.dead ?? [])
    done = lsGet<string[]>(LS_DONE, [])
    recompute()
  })()
  return loaded
}

/* ────────────────────────────────────────────────────────────────────────────
 * Enqueue
 * ────────────────────────────────────────────────────────────────────────── */

export type EnqueueInput = Omit<OutboxItem, 'createdAt' | 'attempts' | 'nextAttemptAt' | 'lastError' | 'dead'>
  & { createdAt?: number }

/**
 * Add one write to the queue.
 *
 * Returns false when the id is already queued or was recently sent — which is
 * the double-tap guard, and the reason `id` is a caller's `client_id` rather
 * than something generated in here.
 */
export async function enqueue(input: EnqueueInput): Promise<boolean> {
  await load()
  if (items.some((i) => i.id === input.id)) return false
  if (done.includes(input.id)) return false

  const item: OutboxItem = {
    ...input,
    createdAt: input.createdAt ?? Date.now(),
    attempts: 0,
    nextAttemptAt: 0,
    lastError: null,
  }
  items.push(item)
  await persist(item)
  recompute()
  start()
  void flush()
  return true
}

/* ────────────────────────────────────────────────────────────────────────────
 * Flush
 * ────────────────────────────────────────────────────────────────────────── */

function fillAliases(text: string): { text: string; missing: string | null; dead: string | null } {
  let missing: string | null = null
  let dead: string | null = null
  const filled = text.replace(/\{\{([a-zA-Z0-9_.-]+)\}\}/g, (_match, name: string) => {
    if (deadAliases.has(name)) {
      dead = name
      return `{{${name}}}`
    }
    const value = aliases[name]
    if (value === undefined) {
      missing = name
      return `{{${name}}}`
    }
    return encodeURIComponent(value)
  })
  return { text: filled, missing, dead }
}

/**
 * A refusal that retrying cannot fix.
 *
 * 408, 425 and 429 are 4xx but are explicitly "try again"; everything else in
 * that range means the request itself is wrong and will be wrong forever. 401 is
 * the interesting exception: the session expired, signing in again fixes it, so
 * it is treated as retryable and the item waits.
 */
function isPermanent(status: number): boolean {
  if (status === 408 || status === 425 || status === 429 || status === 401) return false
  return status >= 400 && status < 500
}

function backoffMs(attempts: number): number {
  const base = Math.min(30_000 * 2 ** Math.max(0, attempts - 1), 10 * 60_000)
  // Jitter, so several queued items released by one `online` event do not all
  // retry on the same millisecond and rebuild the thundering herd.
  return base + Math.floor(Math.random() * 5_000)
}

/**
 * Multipart, so this cannot go through `api()` — that helper stamps
 * `Content-Type: application/json` on any request with a body, and a multipart
 * POST with that header has no boundary and fails on the server every time.
 * Everything else about the call is the same: same-origin credentials, JSON
 * error shape read back.
 */
async function uploadBlob(blob: Blob, filename: string, purpose: string): Promise<string> {
  const form = new FormData()
  form.append('file', blob, filename)
  form.append('purpose', purpose)
  const res = await fetch('/api/files', { method: 'POST', body: form, credentials: 'same-origin' })
  const text = await res.text()
  let parsed: { id?: string; error?: string; code?: string } | null = null
  try {
    parsed = text ? (JSON.parse(text) as { id?: string; error?: string; code?: string }) : null
  } catch {
    throw Object.assign(new Error('The upload endpoint did not answer with JSON.'), { status: res.status })
  }
  if (!res.ok || !parsed?.id) {
    throw Object.assign(new Error(parsed?.error ?? `Upload failed (${res.status}).`), { status: res.status })
  }
  return parsed.id
}

async function sendOne(item: OutboxItem): Promise<void> {
  const uploaded = [...(item.uploaded ?? [])]

  for (let i = uploaded.length; i < (item.photos?.length ?? 0); i += 1) {
    const key = item.photos?.[i]
    if (!key) continue
    const photo = await photoQueue.get(key)
    if (!photo) {
      // The bytes are gone — an eviction, or a different device drained them.
      // The observation itself is still worth recording, so the entry goes on
      // without its photo rather than blocking the whole queue behind it.
      continue
    }
    uploaded.push(await uploadBlob(photo.blob, `${key}.jpg`, item.purpose ?? 'field'))
    // Recorded after EACH upload, not at the end: eight photos and a drop on
    // the seventh must not re-send the first six.
    item.uploaded = [...uploaded]
    await persist(item)
  }

  const body: Record<string, unknown> = { ...item.body }
  if (item.photoField === 'uploadId' && uploaded[0]) body.uploadId = uploaded[0]
  if (item.photoField === 'images') body.images = uploaded.map((id) => ({ uploadId: id }))

  const filled = fillAliases(item.path)
  const response = await api<Record<string, unknown>>(filled.text, {
    method: item.method,
    body: JSON.stringify(body),
  })

  if (item.provides) {
    const value = response?.[item.provides.field]
    if (typeof value === 'string' && value) aliases[item.provides.alias] = value
  }

  for (const key of item.photos ?? []) await photoQueue.remove(key)

  done = [item.id, ...done.filter((x) => x !== item.id)].slice(0, DONE_RING)
  await forget(item.id)
  await persist()
}

/**
 * Drain what can be drained, in order.
 *
 * Strictly FIFO and stopping at the first retryable failure, because order is
 * causality here: the inspection must exist before its entries, and an entry
 * sent before a note about the same item makes the timeline lie.
 */
export async function flush(options: { force?: boolean } = {}): Promise<void> {
  await load()
  if (flushing) {
    flushWanted = true
    return
  }
  if (!items.length) return
  if (!options.force && !isOnline()) return

  flushing = true
  recompute()
  try {
    const now = Date.now()
    for (const item of pendingItems()) {
      if (item.dead) continue
      if (item.nextAttemptAt > now && !options.force) return

      const filled = fillAliases(item.path)
      if (filled.dead) {
        item.dead = true
        item.lastError = `Waiting on something the server refused (${filled.dead}).`
        await persist(item)
        continue
      }
      if (filled.missing) {
        // The provider is ahead of us in the queue and has not answered yet.
        // Stopping rather than skipping keeps the order honest.
        return
      }

      try {
        await sendOne(item)
        lastError = null
      } catch (err) {
        const status = (err as ApiError | undefined)?.status ?? 0
        item.attempts += 1
        item.lastError = err instanceof Error ? err.message : 'Could not send.'
        lastError = item.lastError

        if (isPermanent(status) || item.attempts >= MAX_ATTEMPTS) {
          item.dead = true
          if (item.provides) deadAliases.add(item.provides.alias)
          await persist(item)
          recompute()
          // A permanently refused item does not block the rest of the queue.
          continue
        }

        item.nextAttemptAt = Date.now() + backoffMs(item.attempts)
        await persist(item)
        return
      } finally {
        recompute()
      }
    }
  } finally {
    flushing = false
    recompute()
    if (flushWanted) {
      flushWanted = false
      // Not recursion that can run away: `flushWanted` is only ever set by a
      // call that arrived DURING a pass, and this consumes it before starting.
      void flush(options)
    }
  }
}

/** Try a refused item again — after signing in, or after fixing the cause. */
export async function retry(id: string): Promise<void> {
  await load()
  const item = items.find((i) => i.id === id)
  if (!item) return
  item.dead = false
  item.attempts = 0
  item.nextAttemptAt = 0
  item.lastError = null
  if (item.provides) deadAliases.delete(item.provides.alias)
  await persist(item)
  recompute()
  void flush({ force: true })
}

/** Throw one away, on purpose, with the person looking at it. */
export async function discard(id: string): Promise<void> {
  await load()
  const item = items.find((i) => i.id === id)
  for (const key of item?.photos ?? []) await photoQueue.remove(key)
  await forget(id)
  await persist()
  recompute()
}

/* ────────────────────────────────────────────────────────────────────────────
 * When it runs
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * On `online`, on focus, and every twenty seconds while there is something to
 * send. All three, because none is reliable alone: `online` fires on a Wi-Fi
 * association that has no route to the internet, a backgrounded iOS tab gets no
 * timers at all, and a phone in a pocket gets no focus event.
 */
export function start(): void {
  if (wired || typeof window === 'undefined') return
  wired = true
  const kick = () => {
    void flush()
  }
  window.addEventListener('online', () => {
    recompute()
    kick()
  })
  window.addEventListener('offline', recompute)
  window.addEventListener('focus', kick)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') kick()
  })
  timer = setInterval(() => {
    if (items.length) kick()
  }, FLUSH_EVERY_MS)
  void load().then(kick)
}

/** Test seam. */
export function _resetForTests(): void {
  items = []
  aliases = {}
  deadAliases = new Set()
  done = []
  loaded = null
  flushing = false
  lastError = null
  usingIdb = false
  snapshot = { pending: 0, failed: 0, sending: false, online: true, oldestAt: null, lastError: null, durable: false }
  if (timer) clearInterval(timer)
  timer = null
  wired = false
  listeners.clear()
}
